import React, { useEffect, useState } from 'react'
import ModuleForm from './form'
import ModuleTable from './table'
import SecurityAdministrationLayout from '../../commons/SecurityAdministrationLayout'
import { getFromApi, postToApi, putToApi } from 'actions'
import { VALUES } from 'utils/constants'
import { buildFiltersDescriptors } from '../../commons/SearchFilters'
import omit from 'lodash/omit'
import { useStateValue } from 'context/store'
import { useApiRequest } from 'utils/customHooks/useApiRequest'
import { openSnackBarAction } from 'context/reducers/openSnackbarReducer'

const INITIAL_MODULE_FORM_STATE = {
   name: '',
   status: '',
   description: '',
   application: '',
   link: '',
   id: ''
}

const TITLE = {
   [VALUES.FORM_BEHAVIOR.REGISTER]: 'Registrar Módulo',
   [VALUES.FORM_BEHAVIOR.SEARCH]: 'Consultar Módulo',
   [VALUES.FORM_BEHAVIOR.EDIT]: 'Editar Módulo'
}

const Module = ({ isSearch }) => {
   const [isShowingPopover, setPopoverProp] = useState(true)
   const [applications, setApplications] = useState([])

   const [formData, setFormData] = useState(INITIAL_MODULE_FORM_STATE)
   const [formBehavior, setFormBehavior] = useState(
      isSearch ? VALUES.FORM_BEHAVIOR.SEARCH : VALUES.FORM_BEHAVIOR.REGISTER
   )
   const [tableData, setTableData] = useState(VALUES.TABLE_DATA_INTIAL_STATE)
   const [paginationParams, setPaginationParams] = useState(VALUES.PAGING_ORDER_INITIAL_STATE)
   const [searchFilters, setSearchFilters] = useState({})
   const [options, setOptions] = useState([...VALUES.STATUS_OPTIONS])

   const [_, dispatch] = useStateValue()

   // ---------------- Clean Form -----------------
   function cleanForm() {
      setFormData({
         ...INITIAL_MODULE_FORM_STATE
      })
      setFormBehavior(isSearch ? VALUES.FORM_BEHAVIOR.SEARCH : VALUES.FORM_BEHAVIOR.REGISTER)
   }

   function clean() {
      setSearchFilters({})
      setTableData(VALUES.TABLE_DATA_INTIAL_STATE)
      setPopoverProp(true)
      setPaginationParams(VALUES.PAGING_ORDER_INITIAL_STATE)
      cleanForm()
   }

   useEffect(() => {
      clean()
   }, [isSearch]) // eslint-disable-line react-hooks/exhaustive-deps

   // ------------- Fetch all Initial Active Applications  -----------------
   async function _getAllActiveApplications() {
      const url = `${process.env.REACT_APP_APPLICATIONS_API}/applications/active`
      const activeApplications = await getFromApi(url)
      setApplications(activeApplications)
      const applicationsFilters = activeApplications.map(app => ({
         label: app.name,
         value: app.id
      }))
      setOptions(prev => [...prev, ...applicationsFilters])
   }

   const getAllActiveApplications = useApiRequest(_getAllActiveApplications, 'Módulos: Consultar Aplicaciones Activas')

   useEffect(() => {
      getAllActiveApplications()
   }, [])

   // ------------ Search Modules -----------------
   function getFiltersDescriptors(filters) {
      const { application, ...otherFilters } = filters
      const descriptors =
         buildFiltersDescriptors(options)({
            ...otherFilters,
            application: application?.id
         }) || []
      return descriptors
   }

   async function removeSearchFilter(filter) {
      const remainingFilters = omit(searchFilters, [filter.filterName])
      await searchModules(remainingFilters, VALUES.PAGING_ORDER_INITIAL_STATE)
   }

   async function _searchModules(externalFilters, pagingFromTableParams = {}) {
      const filters = externalFilters ? externalFilters : searchFilters
      const paginationFilters = {
         ...paginationParams,
         ...pagingFromTableParams
      }
      setSearchFilters(filters)
      setPaginationParams(paginationFilters)
      const data = {}
      if (filters.name) {
         data.name = filters.name
      }
      if (filters.application) {
         data.applicationId = filters.application.id
      }
      if (filters.status) {
         data.status = filters.status
      }
      const payload = { ...data, ...paginationFilters }
      const url = `${process.env.REACT_APP_MODULES_API}/modules/search`
      const searchedModules = await getFromApi(url, payload)
      setTableData(searchedModules)
      setFormData(INITIAL_MODULE_FORM_STATE)
   }

   const searchModules = useApiRequest(_searchModules, 'Módulos: Consultar')

   // ------------ Register Modules -----------------
   async function _registerModule() {
      const payload = {
         name: formData.name,
         description: formData.description,
         applicationId: formData.application.id,
         link: formData.link
      }
      const url = `${process.env.REACT_APP_MODULES_API}/modules`
      const registeredModule = await postToApi(url, payload)
      setTableData({
         ...VALUES.TABLE_DATA_INTIAL_STATE,
         data: [registeredModule]
      })
      setFormData(INITIAL_MODULE_FORM_STATE)
      dispatch(openSnackBarAction(`Se guardó correctamente el Módulo ${registeredModule.name}`, 'info'))
   }

   const registerModule = useApiRequest(_registerModule, 'Módulos: Registrar')

   // -------------- Edit Modules ----------------
   async function _editModule() {
      const payload = {
         name: formData.name,
         description: formData.description,
         applicationId: formData.application.id,
         link: formData.link,
         status: formData.status
      }
      const url = `${process.env.REACT_APP_MODULES_API}/modules/${formData.id}`
      const editedModule = await putToApi(url, payload)
      const formBehavior = isSearch ? VALUES.FORM_BEHAVIOR.SEARCH : VALUES.FORM_BEHAVIOR.REGISTER
      setFormBehavior(formBehavior)
      setFormData(INITIAL_MODULE_FORM_STATE)
      setTableData({
         ...VALUES.TABLE_DATA_INTIAL_STATE,
         data: [editedModule]
      })
      dispatch(openSnackBarAction(`Se actualizó correctamente el Módulo ${editedModule.name}`, 'info'))
   }

   const editModule = useApiRequest(_editModule, 'Módulos: Editar')

   // -------------- Form Actions ---------------
   const actionSubmit = type => {
      setPopoverProp(false)
      if (type === VALUES.FORM_BEHAVIOR.REGISTER) {
         registerModule()
      }
      if (type === VALUES.FORM_BEHAVIOR.EDIT) {
         editModule()
      }
      if (type === VALUES.FORM_BEHAVIOR.SEARCH) {
         searchModules(formData, VALUES.PAGING_ORDER_INITIAL_STATE)
      }
   }

   const actionEdit = rowData => {
      setFormBehavior(VALUES.FORM_BEHAVIOR.EDIT)
      setPopoverProp(true)
      setFormData({
         id: rowData.id,
         name: rowData.name,
         description: rowData.description,
         application: rowData.application,
         link: rowData.link,
         status: rowData.status
      })
   }

   return (
      <SecurityAdministrationLayout
         title={TITLE[formBehavior]}
         form={
            <ModuleForm
               formData={formData}
               setForm={setFormData}
               applications={applications}
               actionSubmit={actionSubmit}
               formBehavior={formBehavior}
               cleanCallback={cleanForm}
            />
         }
         table={
            <ModuleTable
               isSearch={isSearch}
               order={paginationParams.order}
               modules={tableData}
               searchModules={searchModules}
               actionEdit={actionEdit}
               cleanCallback={clean}
            />
         }
         popoverProp={isShowingPopover}
         setPopoverProp={setPopoverProp}
         searchFilters={getFiltersDescriptors(searchFilters)}
         onRemoveFilter={removeSearchFilter}
      />
   )
}

export default Module
